import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface Metrique {
  libelle: string;
  valeur: string;
  detail?: string;
  /**
   * Remplace le nombre et le detail par un contenu propre — la
   * repartition des ages, avec ses deux icones. Le titre et son renvoi
   * restent ceux de la tuile ; `valeur` ne sert alors qu'a decider si le
   * renvoi s'affiche.
   */
  contenu?: ReactNode;
  /** `alerte` pour ce qui appelle une action, `succes` pour de l'argent rentré. */
  ton?: 'neutre' | 'succes' | 'alerte';
  /** Vers l'écran qui traite le sujet. Porté par le TITRE de la tuile. */
  lien?: string;
  /** Alternative au lien : filtre la liste sur place. Porté par le titre aussi. */
  action?: () => void;
}

/**
 * Bandeau de métriques en tête d'une liste.
 *
 * Quatre tuiles, toujours dans le même ordre sur les trois écrans, et
 * la DERNIÈRE est toujours celle qui appelle une action — fiches à
 * compléter, inscriptions sans créneau, reste à encaisser. C'est la
 * position qu'on apprend à regarder.
 *
 * Quand une tuile mène quelque part, c'est son TITRE qui porte le lien :
 * une ligne d'action sous le nombre ajoutait un troisième niveau de
 * lecture pour dire ce que le titre disait déjà. Le nombre, lui, ne
 * clique jamais — un chiffre qui se comporte parfois comme un bouton ne
 * s'apprend pas.
 *
 * Les tailles reprennent celles du tableau de bord : les deux écrans se
 * lisent l'un après l'autre, ils doivent se lire pareil.
 */
export function BandeauMetriques({ metriques }: { metriques: Metrique[] }) {
  return (
    <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {metriques.map((metrique) => (
        <Tuile key={metrique.libelle} {...metrique} />
      ))}
    </div>
  );
}

function Tuile({ libelle, valeur, detail, contenu, ton = 'neutre', lien, action }: Metrique) {
  const couleurs = {
    neutre: 'text-encre-900',
    succes: 'text-emerald-700',
    alerte: 'text-red-700',
  } as const;

  // Une tuile a zéro n'appelle plus rien : le renvoi disparaît avec le
  // problème qu'il servait à traiter.
  const renvoyable = valeur !== '0' && valeur !== '0,00 €';
  const classeTitre = 'text-xs font-medium uppercase tracking-wide text-encre-400';

  // Le titre vit TOUJOURS dans le meme bloc, que la tuile renvoie
  // quelque part ou non. Un lien pose directement dans la carte est un
  // element en ligne : la hauteur de sa ligne vient de la carte, pas de
  // sa propre taille de texte, et la tuile cliquable descendait donc
  // d'un cran par rapport aux trois autres. L'element interactif passe
  // A L'INTERIEUR du bloc de titre, qui garde seul la typographie.
  //
  // Le bouton porte en plus `uppercase`, et ce n'est pas une redite du
  // bloc de titre : une valeur HERITEE perd contre la declaration que le
  // navigateur pose lui-meme sur les controles de formulaire
  // (`text-transform: none`). Un lien s'en tire, pas un bouton — « Sans
  // creneau » se lisait en minuscules a cote de titres en capitales.
  return (
    <div className={`carte h-full p-5 ${ton === 'alerte' && renvoyable ? 'border-red-200' : ''}`}>
      <div className={classeTitre}>
        {renvoyable && lien ? (
          <Link to={lien} className="underline">
            {libelle}
          </Link>
        ) : renvoyable && action ? (
          <button type="button" className="text-left uppercase underline" onClick={action}>
            {libelle}
          </button>
        ) : (
          libelle
        )}
      </div>

      {contenu ?? (
        <>
          <div className={`tabulaire mt-2 text-3xl font-semibold ${couleurs[ton]}`}>{valeur}</div>
          {detail && <div className="mt-1 text-xs text-encre-500">{detail}</div>}
        </>
      )}
    </div>
  );
}
